import { useState } from "react";
import { useUpdateInventoryItem } from "../../hooks/useInventory";
import { Button } from "@tonic/shared/components/ui";
import { Input } from "@tonic/shared/components/ui";
import { Label } from "@tonic/shared/components/ui";
import { Textarea } from "@tonic/shared/components/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@tonic/shared/components/ui";
import PropTypes from 'prop-types';

/**
 * @typedef {Object} InventoryItem
 * @property {string} id - Item ID
 * @property {string} name - Item name
 * @property {string} sku - Item SKU
 * @property {string} description - Item description
 * @property {string} category - Item category
 * @property {Object} specifications - Item specifications
 */

/**
 * @typedef {Object} EditItemDialogProps
 * @property {InventoryItem} item - The inventory item to edit
 * @property {boolean} open - Whether the dialog is open
 * @property {(open: boolean) => void} onOpenChange - Callback when dialog open state changes
 */

/**
 * Dialog component for editing inventory item details
 * @param {EditItemDialogProps} props
 * @returns {JSX.Element} The edit item dialog component
 */
export function EditItemDialog({ item, open, onOpenChange }) {
  const updateItem = useUpdateInventoryItem(item.id);
  const [formData, setFormData] = useState({
    name: item.name,
    sku: item.sku,
    description: item.description || "",
    category: item.category,
    specifications: JSON.stringify(item.specifications || {}, null, 2),
  });

  /**
   * Handles form submission to update the item
   * @param {React.FormEvent} e - Form event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateItem.mutateAsync({
        ...formData,
        specifications: JSON.parse(formData.specifications),
      });
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update item:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
            <DialogDescription>
              Update the details of this inventory item.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, sku: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, category: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="specifications">Specifications (JSON)</Label>
              <Textarea
                id="specifications"
                value={formData.specifications}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    specifications: e.target.value,
                  }))
                }
                rows={6}
                className="font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateItem.isLoading}>
              {updateItem.isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

EditItemDialog.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    sku: PropTypes.string.isRequired,
    description: PropTypes.string,
    category: PropTypes.string.isRequired,
    specifications: PropTypes.object,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}; 